import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QuotationForm from './Quotationform';
import SummaryPanel from './SummaryPanel';
import MaterialTable from './MaterialTable';
import styles from './Cotizador.module.scss';
import { getQuotationById, getMaterialsBySubquotationId, subQuotationPostulation } from '../../../store/actions/profileActions';
import BotoneraModern from '../../Botonera/BotoneraModern.tsx';
import RegisteredUserProfileLoader from '../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader';
import useNavVisibility from '../../../hooks/useNavVisibility.ts';
import { ExternalLink } from 'lucide-react';
import ModalOverlay from '../../UI/ModalOverlay/ModalOverlay.tsx';
import Breadcrumbs from '../../UI/Breadcrumbs/Breadcrumbs.tsx';


const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin-bottom: 10px;
  padding-right: 5%;
  padding-left: 5%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
  }

  @media (max-width: 768px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

interface MaterialEntry {
  id?: number | string;
  name: string;
  unit?: string;
  amount?: number | string | null;
  priceUnit?: number | string | null;
  discount?: number | string | null;
}

interface MaterialCategoryRaw {
  category_id: number | string;
  name: string;
  materials?: MaterialEntry[];
}

interface MaterialCategory {
  category_id: number | string;
  name: string;
  materials: MaterialEntry[];
}

type MaterialsBySubquotation = Record<string, MaterialCategoryRaw[]>;

interface Totals {
  subtotal: number;
  iva: number;
  total: number;
}

interface DataLoadedState {
  quotation: boolean;
  materials: boolean;
}

interface QuotationUser {
  type?: string;
  razon_social?: string;
  name?: string;
  last_name?: string;
  username?: string;
  phone?: string;
  image_profile?: string | null;
  id?: number | string;
}

interface QuotationInfo {
  id: number;
  name?: string;
  user?: QuotationUser | null;
  endDate?: string | null;
  active?: boolean | null;
}

interface SubQuotation {
  id: number;
}

interface QuotationData {
  quotation?: QuotationInfo | null;
  subquotation?: SubQuotation[];
}

interface RootState {
  profile: {
    quotation?: QuotationData | null;
    materialsBySubquotation?: MaterialsBySubquotation | null;
    loading?: boolean;
    error?: string | null;
  };
  general: {
    userData?: QuotationUser | null;
  };
}

interface CotizadorProps {
  quotationData?: QuotationData | null;
  materialsBySubquotation?: MaterialsBySubquotation | null;
  loading?: boolean;
  error?: string | null;
  userData?: QuotationUser | null;
  getQuotationById: (id: string, isEditing?: boolean, publicQuotation?: unknown) => Promise<void> | void;
  getMaterialsBySubquotationId: (id: number | string) => Promise<void> | void;
  subQuotationPostulation: (
    subQuotationId: number | string,
    file: File,
    note: string,
    endDate: string,
    navigate: NavigateFunction
  ) => void;
}

const CotizadorNew: React.FC<CotizadorProps> = ({
  quotationData,
  materialsBySubquotation,
  loading,
  error,
  userData,
  getQuotationById,
  getMaterialsBySubquotationId,
  subQuotationPostulation
}) => {
  const [materials, setMaterials] = useState<MaterialCategory[]>([]);
  const [totals, setTotals] = useState<Totals>({ subtotal: 0, iva: 0, total: 0 });
  const [endDate, setEndDate] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<DataLoadedState>({ quotation: false, materials: false });

  const materialsRef = useRef<MaterialsBySubquotation | null>(null);
  const navigate = useNavigate();
  const { quotationId } = useParams<{ quotationId: string }>();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();

  const calculateTotals = useCallback((updatedMaterials: MaterialCategory[]) => {
    let subtotal = 0;

    updatedMaterials.forEach((category) => {
      category.materials.forEach((item) => {
        const amount = Number(item.amount) || 0;
        const priceUnit = Number(item.priceUnit) || 0;
        const discount = Number(item.discount) || 0;
        const totalPrice = amount * priceUnit * (1 - discount / 100);
        subtotal += totalPrice;
      });
    });

    const roundedSubtotal = Number(subtotal.toFixed(2));
    const iva = Number((roundedSubtotal * 0.21).toFixed(2));
    const total = Number((roundedSubtotal + iva).toFixed(2));

    setTotals({ subtotal: roundedSubtotal, iva, total });
  }, []);

  useEffect(() => {
    if (!quotationId) return;

    const fetchData = async () => {
      try {
        await getQuotationById(quotationId, false, null);
      } catch (fetchError) {
        console.error('Error fetching quotation:', fetchError);
      } finally {
        setIsDataLoaded((prev) => ({ ...prev, quotation: true }));
      }
    };

    fetchData();
  }, [quotationId, getQuotationById]);

  const firstSubquotationId = quotationData?.subquotation?.[0]?.id;

  useEffect(() => {
    if (!firstSubquotationId) return;

    const fetchMaterials = async () => {
      try {
        await getMaterialsBySubquotationId(firstSubquotationId);
      } catch (fetchError) {
        console.error('Error fetching materials:', fetchError);
      } finally {
        setIsDataLoaded((prev) => ({ ...prev, materials: true }));
      }
    };

    fetchMaterials();
  }, [firstSubquotationId, getMaterialsBySubquotationId]);

  useEffect(() => {
    if (!materialsBySubquotation) return;
    if (materialsRef.current === materialsBySubquotation) return;

    materialsRef.current = materialsBySubquotation;

    const transformedCategories = Object.values(materialsBySubquotation)
      .flat()
      .filter(Boolean) as MaterialCategoryRaw[];

    const formattedMaterials: MaterialCategory[] = transformedCategories.map((category) => ({
      category_id: category.category_id,
      name: category.name,
      materials: Array.isArray(category.materials) ? category.materials : []
    }));

    setMaterials(formattedMaterials);
    calculateTotals(formattedMaterials);
  }, [materialsBySubquotation, calculateTotals]);

  const allDataLoaded = useMemo(
    () => Object.values(isDataLoaded).every((value) => value),
    [isDataLoaded]
  );

  useEffect(() => {
    if (allDataLoaded) {
      setIsLoading(false);
    }
  }, [allDataLoaded]);

  const handleMaterialsChange = (updatedMaterials: MaterialCategory[]) => {
    setMaterials(updatedMaterials);
    calculateTotals(updatedMaterials);
  };

  const formatQuotationUserName = (user?: QuotationUser | null) => {
    if (!user) return 'Usuario sin datos';
    if (user.type === 'COMPANY' && user.razon_social) return user.razon_social;
    const fullName = `${user.name ?? ''} ${user.last_name ?? ''}`.trim();
    return fullName.length > 0 ? fullName : 'Usuario sin datos';
  };

  const generatePdf = (
    materialsToExport: MaterialCategory[],
    totalsToExport: Totals,
    noteToExport: string,
    validUntil: string,
    generatorUser?: QuotationUser | null
  ) => {
    const doc = new jsPDF();

    const formatDate = (date: string) => {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    };

    const generatorName = formatQuotationUserName(generatorUser);
    const generatorEmail = generatorUser?.username ?? 'No disponible';
    const generatorPhone = generatorUser?.phone ?? 'No disponible';

    doc.setFontSize(16);
    doc.text('Presupuesto Detallado', 14, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(`Generado por: ${generatorName}`, 14, 30);
    doc.text(`Correo: ${generatorEmail}`, 14, 36);
    doc.text(`Teléfono: ${generatorPhone}`, 14, 42);

    const validUntilText = validUntil ? `Presupuesto válido hasta: ${formatDate(validUntil)}` : '';
    if (validUntilText) {
      doc.text(validUntilText, 14, 48);
    }

    const tableStartY = 55;

    autoTable(doc, {
      head: [['Material', 'Categoría', 'Unidad', 'Cantidad', 'Precio Unitario', 'Descuento', 'Subtotal']],
      body: materialsToExport.flatMap((category) =>
        category.materials.map((material) => {
          const unitPrice = Number(material.priceUnit) || 0;
          const amount = Number(material.amount) || 0;
          const discount = Number(material.discount) || 0;
          const subtotal = amount * unitPrice * (1 - discount / 100);

          return [
            material.name,
            category.name,
            material.unit ?? '',
            amount || 0,
            unitPrice ? `$${unitPrice.toFixed(2)}` : 'No cotizado',
            `${discount.toFixed(2)}%`,
            `$${subtotal.toFixed(2)}`
          ];
        })
      ),
      startY: tableStartY,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontSize: 12 },
      bodyStyles: { fontSize: 10 },
      columnStyles: {
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' }
      }
    });

    const previousTable = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
    const summaryStartY = previousTable ? previousTable.finalY + 10 : tableStartY + 10;

    autoTable(doc, {
      body: [
        ['Subtotal', `$${totalsToExport.subtotal.toFixed(2)}`],
        ['IVA (21%)', `$${totalsToExport.iva.toFixed(2)}`],
        ['Total', `$${totalsToExport.total.toFixed(2)}`]
      ],
      startY: summaryStartY,
      theme: 'grid',
      bodyStyles: { fontSize: 10, halign: 'right' },
      columnStyles: { 0: { halign: 'left' } }
    });

    if (noteToExport) {
      const noteTable = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
      const noteStartY = noteTable ? noteTable.finalY + 10 : summaryStartY + 20;
      doc.setFontSize(12);
      doc.text('Condiciones Especiales', 14, noteStartY);
      doc.setFontSize(10);
      doc.text(noteToExport, 14, noteStartY + 10);
    }

    const footerText =
      'Este presupuesto tiene carácter informativo y no es una oferta formal. Los precios y condiciones no están garantizados por Edify.';
    const footerY = doc.internal.pageSize.getHeight() - 20;
    const footerTextWidth = doc.getTextWidth(footerText);
    const footerX = doc.internal.pageSize.getWidth() - 14 - footerTextWidth;

    doc.setFontSize(9);
    doc.text(footerText, footerX, footerY);

    const pdfBlob = doc.output('blob');
    return new File([pdfBlob], 'presupuesto.pdf', { type: 'application/pdf' });
  };

  const handleSendQuotation = () => {
    const subquotationId = quotationData?.subquotation?.[0]?.id;
    if (!subquotationId) return;

    const pdfFile = generatePdf(materials, totals, note, endDate, userData);
    setShowSummary(false);
    subQuotationPostulation(subquotationId, pdfFile, note, endDate, navigate);
  };


  const applicantName = formatQuotationUserName(quotationData?.quotation?.user);
  const applicantInitials = applicantName
    .split(' ')
    .filter(Boolean)
    .map((segment) => segment[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Container>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      {isLoading ? (
        <RegisteredUserProfileLoader smallVersion={false} noBackground={false} />
      ) : (
        <div className={styles.page}>
          <Breadcrumbs />
          <section className={styles.hero}>
            <div className={styles.heroPrimary}>
              <span className={styles.heroBadge}>Cotización privada</span>
              <h1 className={styles.heroTitle}>{quotationData?.quotation?.name ?? 'Cotización sin nombre'}</h1>
            </div>
            <aside className={styles.ownerCard}>
              <span className={styles.ownerHeading}>Solicitante</span>
              <div className={styles.ownerInfo}>
                <div className={styles.ownerAvatar}>
                  {quotationData?.quotation?.user?.image_profile ? (
                    <img src={quotationData.quotation.user.image_profile} alt={applicantName} />
                  ) : (
                    <span>{applicantInitials || 'ED'}</span>
                  )}
                </div>
                <div className={styles.ownerDetails}>
                  <span className={styles.ownerName}>{applicantName}</span>
                  {quotationData?.quotation?.user?.id && (
                    <a
                      className={styles.ownerLink}
                      href={`/perfil/${quotationData.quotation.user.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver perfil
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            </aside>
          </section>

          <div className={styles.layout}>
            <div className={styles.primaryColumn}>
              <section className={styles.card}>
                <QuotationForm quotation={quotationData} />
              </section>
              <section className={styles.card}>
                <MaterialTable
                  materials={materials}
                  onMaterialsChange={handleMaterialsChange}
                  setShowSummary={setShowSummary}
                  isMyQuotation={false}
                  totals={totals.total}
                  isEditable={true}
                />
              </section>
            </div>
          </div>

          <ModalOverlay isOpen={showSummary} onClose={() => setShowSummary(false)} preventClose={false} zIndex={20}>
            <SummaryPanel
              totals={totals}
              handleSendQuotation={handleSendQuotation}
              endDate={endDate}
              note={note}
              setNote={setNote}
              closeModal={() => setShowSummary(false)}
              setEndDate={setEndDate}
            />
          </ModalOverlay>
        </div>
      )}
    </Container>
  );
};

const mapStateToProps = (state: RootState) => ({
  quotationData: state.profile.quotation,
  materialsBySubquotation: state.profile.materialsBySubquotation,
  loading: state.profile.loading,
  error: state.profile.error,
  userData: state.general.userData 
});

const mapDispatchToProps = {
  getQuotationById,
  getMaterialsBySubquotationId,
  subQuotationPostulation
};

export default connect(mapStateToProps, mapDispatchToProps)(CotizadorNew);
